import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useHistory, Link } from 'react-router-dom';
import { Header, EmptyList, Cookies } from 'Components/Common';
import { useAtom } from 'jotai';
import {
  ticketAtom,
  amountToPayAtom,
  displayShareTicketAtom,
  payPlusSessionAtom,
  isIOSDeviceAtom,
  goToPayAtom,
} from 'Atoms/Ticket';
import { cookiesAtom, staticDataAtom, displayCookiesAtom } from 'Atoms/Common';
import { Typography, Grid, makeStyles, Button } from '@material-ui/core';
import { ListProductReadOnly } from '../Product';
import { TotalLow, TotalLite, SubTotal } from '../Total';
import Discount from '../Discount';
import PaymentList from '../PaymentList';
import { BottomContainerAction, ButtonActions, Loader } from 'Components/UI';
import { useTranslation } from 'react-i18next';
import {
  getTicketAliasFromPathname,
  formattingTimeWithoutSeconde,
  formattingDate,
  formattingDigitCurrency,
  paymentProcessCompleted,
} from 'utils';
import PaymentListWaiting from '../PaymentWaitingList/PaymentWaitingList';
import { ShareContainer, Share } from '../Share';
import { useWindowSize } from 'Components/Hooks';

const useStyles = makeStyles((theme) => ({
  root: {
    ...theme.containerGrid,
    /* overflowY: 'scroll',
    '-webkit-overflow-scrolling': 'touch', */
    //'& .container:nth-last-child(2)': { marginBottom: '20rem' },
    position: 'relative',
  },
  subHeader: {
    margin: '1rem',
    background: '#fff',
    borderRadius: '1rem',
    padding: '1rem 1rem 0.5rem 1rem',
  },
  slabel: {
    fontSize: '0.9rem',
    paddingRight: '0.2rem',
  },
  payPanel: {
    margin: '1rem',
    padding: '1rem',
    background: '#fff',
    borderRadius: '1rem',
  },
  shareTicket: {
    margin: '0 1rem 1rem',
    padding: '0.4em 0 0.4rem 1rem',
    background: '#fff',
    borderRadius: '1rem',
  },
  headerTitle: {
    marginBottom: '0.4rem',
  },
  important: {
    fontWeight: 'bold',
  },
  title: { ...theme.subTitle, fontSize: '25px', fontWeight: 300 },
  subTitle2: {
    ...theme.subTitle2,
    marginLeft: '1rem',
    padding: '1rem 0.4rem 0 0.4rem',
  },
  disableTotal: {
    opacity: 0.5,
    pointerEvents: 'none',
  },
  blur: {
    filter: 'blur(4px)',
  },
  paddingBottom: {
    paddingBottom: '8rem',
  },
  minHeight: {
    minHeight: '300px',
  },
  minHeightBigHeight: {
    minHeight: '500px',
  },
  btPay: {
    margin: '0.2rem 0',
    boxShadow: 'none',
  },
  btTips: {
    boxShadow: 'none',
    margin: '0 0 0 0.8rem',
    backgroundColor: '#5ac8f5',
    '&:active, &:hover': {
      backgroundColor: theme.palette.primary.main,
      color: '#fff',
    },
  },
  btShare: {
    boxShadow: 'none',
    backgroundColor: '#5ac8f5',
    margin: '0.2rem 0',
    '&:active, &:hover': {
      backgroundColor: theme.palette.primary.main,
      color: '#fff',
    },
  },
  payDescription: {
    color: '#262957',
  },
  hr: {
    border: '0.4px solid #ccc',
    margin: '0.5rem 0',
  },
}));

const Ticket = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const history = useHistory();

  const [ticket] = useAtom(ticketAtom);
  const [staticData] = useAtom(staticDataAtom);
  const [cookies, setCookies] = useAtom(cookiesAtom);
  const [displayCookies, setDisplayCookies] = useAtom(displayCookiesAtom);
  const [displayShareTicket, setDisplayShareTicket] = useAtom(
    displayShareTicketAtom,
  );
  const [amountToPay] = useAtom(amountToPayAtom);
  const [, setGoToPay] = useAtom(goToPayAtom);

  const { id, order } = ticket || {};
  const { sections, totals, date, table, discount, enablePayment } =
    order || {};
  const { company, askCookies } = staticData || {};
  const currency = staticData?.currency;
  const { countryCode, labelCode } = currency || {};
  const [error, setError] = useState(false);
  const [loader, setLoader] = useState(true);
  const [payPlusSession, setPayPlusSession] = useAtom(payPlusSessionAtom);
  const [checkPaymentIsValidated, setCheckPaymentIsValidated] = useState(false);
  const [isIOSDevice] = useAtom(isIOSDeviceAtom);
  const size = useWindowSize();

  useEffect(() => {
    if (ticket && ticket.error) {
      setError(true);
      setLoader(false);
    }
  }, [ticket]);

  useEffect(() => {
    const pathname = window?.location?.pathname;
    const ticketAlias = getTicketAliasFromPathname(pathname);
    let searchUrl = window?.location?.search;

    if ((ticketAlias !== id && !ticket?.error) || searchUrl === '?errorPay') {
      history.push(`/ticket/:${ticketAlias}`);
      history.go();
    }
    if (searchUrl === '?checkout') {
      paymentIsValidated();
    }

    if (
      (ticketAlias === id && searchUrl !== '?checkout') ||
      (ticketAlias === id && !searchUrl)
    ) {
      const timer = setTimeout(() => {
        setLoader(false);
      }, 150);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    if (!!askCookies) {
      setDisplayCookies(true);
    } else {
      setCookies(true);
    }
  }, [staticData]);

  useEffect(() => {
    if (
      !!enablePayment &&
      totals?.amount === 0 &&
      ticket.odk &&
      ticket.odkUrl
    ) {
      window.location.href = ticket.odkUrl;
    }
  }, [enablePayment, totals, ticket]);

  const paymentIsValidated = async () => {
    const idTransaction = payPlusSession?.idTransaction;
    const pathname = window?.location?.pathname;
    const ticketAlias = getTicketAliasFromPathname(pathname);
    const idSession = staticData?.idSession;

    if (idTransaction) {
      const check = await paymentProcessCompleted(
        idTransaction,
        idSession,
        ticketAlias,
      );
      if (check?.ok && !!check.ok) {
        history.push(`/ticket/:${ticketAlias}`);
        history.go(0);
      } else {
        setTimeout(() => {
          setCheckPaymentIsValidated(false);
          paymentIsValidated();
        }, 500);
      }
    } else {
      history.push(`/ticket/:${ticketAlias}`);
      history.go(0);
    }
  };

  const nbGuests = ticket?.order?.nbGuests || 0;
  const totalAmountToPay = formattingDigitCurrency(
    amountToPay,
    countryCode,
    labelCode,
  );

  const totalAmountOrdered = formattingDigitCurrency(
    totals.ordered,
    countryCode,
    labelCode,
  );
  const totalPaid = ticket?.order?.payments?.done?.total;
  const totalAmountPaid = formattingDigitCurrency(
    totalPaid,
    countryCode,
    labelCode,
  );

  const labelButtonAction =
    amountToPay > 0 ? `${t('pay_bill')}` : `${t('already_paid')}`;

  const paymentsLineExist = ticket?.order?.payments?.done?.total > 0;

  const paymentWaitingListExist = ticket?.order?.payments?.waiting?.total > 0;

  const renderContainer = () => {
    if (error) {
      return <EmptyList label="error_fetch_ticket" />;
    }

    if (!!displayCookies) {
      return (
        <Grid container className={classes.root}>
          <Cookies />
        </Grid>
      );
    }

    const ticketOrderDateFormatted = formattingDate(date, countryCode);
    const ticketOrderTimeFormatted = formattingTimeWithoutSeconde(date);
    const displayDiscount =
      discount?.global > 0 || discount?.products > 0 ? true : false;
    const hasDiscountOrOffererd = discount?.global + discount?.products;

    const tipsDisabled = ticket.tips && ticket.tips.enable === false;
    const redirectToPayment = (e) => {
      e.preventDefault();
      setGoToPay(true);
      setTimeout(() => history.push(`/ticket/:${id}/pay/form`), 500);
    };

    return (
      <>
        <Grid
          container
          className={`${classes.root} ${
            displayShareTicket && '' /*classes.blur*/
          }`}
        >
          <Grid item xs={12} className={classes.subHeader}>
            <Typography>
              {t('Datethe')}
              <span className={classes.important}>
                {ticketOrderDateFormatted}
              </span>{' '}
              {t('at')}
              <span className={classes.important}>
                {ticketOrderTimeFormatted}
              </span>
            </Typography>
            <Typography variant="h6" className={classes.title}>
              {company?.name}
            </Typography>
            <Typography>
              {t('table')}
              {table?.name}
            </Typography>
            <hr className={classes.hr} style={{ marginTop: '1rem' }} />
            {amountToPay > 0 ? (
              <>
                <TotalLow
                  discount={discount}
                  amount={totals.ordered}
                  label={`${t('total')} ${t(':')}`}
                  currency={currency}
                  styles={{ bold: false }}
                />
                <SubTotal
                  amount={totalPaid}
                  label={`${t('paid')} ${t(':')}`}
                  currency={currency}
                />
                <hr className={classes.hr} />
                <TotalLite
                  amount={amountToPay}
                  label={`${t('rest_to_pay')} ${t(':')}`}
                  currency={currency}
                />
              </>
            ) : (
              <TotalLite
                discount={discount}
                amount={totals.ordered}
                label={`${t('total')} ${t(':')}`}
                currency={currency}
                styles={{ bold: false }}
              />
            )}
          </Grid>
          {!!enablePayment ? (
            <Grid item xs={12} className={classes.payPanel}>
              <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                {amountToPay > 0 && !!enablePayment ? (
                  <>
                    <Grid
                      container
                      direction="row"
                      justifyContent="center"
                      alignItems="center"
                    >
                      <Grid item>
                        <Link
                          to={!tipsDisabled ? `/ticket/:${id}/tips` : ''}
                          onClick={(e) =>
                            tipsDisabled ? redirectToPayment(e) : null
                          }
                          className={
                            amountToPay <= 0 ? classes.disableTotal : ''
                          }
                          data-testid="submit"
                        >
                          <Button
                            variant="contained"
                            color={'secondary'}
                            fullWidth={true}
                            className={classes.btPay}
                            size="large"
                          >
                            {labelButtonAction} ({totalAmountToPay})
                          </Button>
                        </Link>
                        <Link
                          to={`/ticket/:${id}/share`}
                          className={
                            amountToPay <= 0 ? classes.disableTotal : ''
                          }
                          data-testid="submit"
                        >
                          <Button
                            variant="contained"
                            color={'secondary'}
                            fullWidth={true}
                            className={classes.btShare}
                            size="large"
                          >
                            {t('pay_a_slice')}
                          </Button>
                        </Link>
                      </Grid>
                    </Grid>
                  </>
                ) : (
                  ''
                )}
                {!!enablePayment && amountToPay === 0 ? (
                  <>
                    <Typography variant="h6" className={classes.payDescription}>
                      {t('billPaid')}
                    </Typography>
                    <Grid
                      container
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Grid item xs={6}>
                        <Typography className={classes.slabel}>
                          {t('giveTips')}
                        </Typography>
                      </Grid>
                      {!tipsDisabled ? (
                        <Grid
                          item
                          xs={6}
                          style={{ paddingLeft: '0.2rem', textAlign: 'right' }}
                        >
                          <Link to={`/ticket/:${id}/tips`} data-testid="submit">
                            <Button
                              variant="contained"
                              color={'secondary'}
                              className={classes.btTips}
                            >
                              {t('tipping')}
                            </Button>
                          </Link>
                        </Grid>
                      ) : null}
                    </Grid>
                  </>
                ) : (
                  ''
                )}
              </Grid>
            </Grid>
          ) : null}
          {nbGuests > 0 ? (
            <Grid item xs={12} className={classes.shareTicket}>
              <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                onClick={() => {
                  setDisplayShareTicket(!displayShareTicket);
                }}
              >
                <Grid item style={{ maxWidth: '70%' }}>
                  {amountToPay === 0 || !enablePayment ? (
                    <Typography>{t('sendTheNote')}</Typography>
                  ) : (
                    <Typography>{t('shareTheNote')}</Typography>
                  )}
                </Grid>
                <Grid item style={{ height: '61px' }}>
                  {displayShareTicket ? (
                    <svg
                      id="SVGDoc"
                      width="53"
                      height="61"
                      xmlns="http://www.w3.org/2000/svg"
                      version="1.1"
                      viewBox="0 0 53 61"
                    >
                      <defs></defs>
                      <g>
                        <g transform="rotate(-90 26.5 30.5)">
                          <title>Path 2</title>
                          <path
                            d="M20,20v0l13,10.5v0l-13,10.5v0"
                            fillOpacity="0"
                            fill="#ffffff"
                            strokeDashoffset="0"
                            strokeDasharray=""
                            strokeLinejoin="miter"
                            strokeLinecap="butt"
                            strokeOpacity="1"
                            stroke="#e84249"
                            strokeMiterlimit="20"
                            strokeWidth="2"
                          ></path>
                        </g>
                      </g>
                    </svg>
                  ) : (
                    <svg
                      id="SVGDoc"
                      width="53"
                      height="61"
                      xmlns="http://www.w3.org/2000/svg"
                      version="1.1"
                      viewBox="0 0 53 61"
                    >
                      <defs></defs>
                      <g>
                        <g transform="rotate(90 26.5 30.5)">
                          <title>Path 2</title>
                          <path
                            d="M20,20v0l13,10.5v0l-13,10.5v0"
                            fillOpacity="0"
                            fill="#ffffff"
                            strokeDashoffset="0"
                            strokeDasharray=""
                            strokeLinejoin="miter"
                            strokeLinecap="butt"
                            strokeOpacity="1"
                            stroke="#e84249"
                            strokeMiterlimit="20"
                            strokeWidth="2"
                          ></path>
                        </g>
                      </g>
                    </svg>
                  )}
                </Grid>
              </Grid>
              <div>{displayShareTicket && <Share />}</div>
            </Grid>
          ) : null}
          <Grid item xs={12}>
            <Grid container direction="column">
              <Grid item xs={12}>
                <Typography className={classes.subTitle2}>
                  {t('details')}
                </Typography>
              </Grid>

              <Grid
                item
                xs={12}
                className={`${
                  !paymentsLineExist &&
                  !paymentWaitingListExist &&
                  !isIOSDevice &&
                  classes.paddingBottom
                }`}
              >
                <ListProductReadOnly
                  listProducts={sections}
                  currency={currency}
                />
                {/*displayDiscount && (
                  <Discount
                    discount={discount}
                    currency={currency}
                    labelCode={labelCode}
                  />
                )*/}
              </Grid>
            </Grid>
          </Grid>
          {paymentsLineExist && (
            <PaymentList
              paddingBottom={paymentWaitingListExist ? false : true}
            />
          )}
          {paymentWaitingListExist > 0 && <PaymentListWaiting />}
          {/* <Grid container justifyContent="center">
          <Grid item xs={11}>
            <ButtonActions
              label={t('receive_ticket')}
              styles={{
                background: 'backgroundWhite',
                label: 'labelDark',
              }}
              color="default"
            />
          </Grid>
          <Grid item xs={11} className={classes.otherActionsMarginButton}>
            <ButtonActions
              label={t('receive_receipt')}
              styles={{
                background: 'backgroundWhite',
                label: 'labelDark',
              }}
              color="default"
            />
          </Grid>
        </Grid> */}
        </Grid>
        {/*<>
        {amountToPay > 0 && !!enablePayment ? (
          <BottomContainerAction>
            <Link
              to={`/ticket/:${id}/pay`}
              className={amountToPay <= 0 ? classes.disableTotal : ''}
              data-testid="submit"
            >
              <ButtonActions label={labelButtonAction} color="secondary" />
            </Link>
          </BottomContainerAction>
        ) : (
          ''
        )}
        {!!enablePayment && amountToPay === 0 ? (
          <BottomContainerAction>
            <Link to={`/ticket/:${id}/tips`} data-testid="submit">
              <ButtonActions label={t('tipping')} color="secondary" />
            </Link>
          </BottomContainerAction>
        ) : (
          ''
        )}
        </>*/}
      </>
    );
  };
  return (
    <>
      {loader && (
        <div id="spinner">
          <Loader />
        </div>
      )}
      {!loader && (
        <>
          <Header
            title={t('my_ticket')}
            actions={
              cookies
                ? {
                    prev: {
                      icon: 'laddition',
                      path: `/restaurant/${company?.name}`,
                      label: '',
                    },
                    next: {
                      /*
                      icon: 'shareTicket',
                      path: ``,
                      label: '',
                      action: 'shareTicket',
                  */
                    },
                  }
                : {}
            }
            /*  actions={{
          name: 'return',
          prev: { label: t('all'), path: `/ticket/:${id}` },
        }} */
          />
          {renderContainer()}
        </>
      )}
      {/*displayShareTicket && <ShareContainer />*/}
    </>
  );
};

Ticket.propTypes = {};

export default Ticket;
